import { useState } from 'react';

import { PlanningState } from '../types/planning';
import { ConceptPart, generateConceptParts } from '../utils/planningHelpers';

interface ConceptPreviewProps {
  planningState: PlanningState;
  onEditField?: (fieldType: ConceptPart['type']) => void;
  isInteractive?: boolean;
  includeSegment2Summary?: boolean;
}

const colorMap: Record<ConceptPart['type'], string> = {
  static: 'text-white',
  // Player group - blue spectrum
  playerRole: 'text-blue-500',
  playerName: 'text-blue-400',
  playerMotivation: 'text-blue-600',
  // NPC group - red spectrum
  npcRole: 'text-red-500',
  npcName: 'text-red-400',
  npcMotivation: 'text-red-600',
  // Setting group - green spectrum
  aPlot: 'text-emerald-500',
  stakes: 'text-emerald-400',
  location: 'text-emerald-600',
  // Dynamic type - distinct color
  dynamicType: 'text-amber-500',
  // B-Plot group - distinct color spectrum
  bPlotSeed: 'text-purple-400',
  bPlotReason: 'text-purple-500',
  bPlotNature: 'text-purple-600',
  bPlotExplanation: 'text-purple-300',
};

const fieldLabels: Record<ConceptPart['type'], string> = {
  static: '',
  playerRole: 'Player Character Role',
  playerName: 'Player Character Name',
  playerMotivation: "Player's Motivation",
  npcRole: 'NPC Role',
  npcName: 'NPC Name',
  npcMotivation: "NPC's Motivation",
  aPlot: 'Central Conflict (A-Plot)',
  stakes: 'Stakes',
  location: 'Location & Atmosphere',
  dynamicType: 'Relationship Dynamic Type',
  bPlotSeed: 'B-Plot Seed',
  bPlotReason: 'B-Plot Reason',
  bPlotNature: 'B-Plot Nature',
  bPlotExplanation: 'B-Plot Explanation',
};

export function ConceptPreview({
  planningState,
  onEditField,
  isInteractive = false,
  includeSegment2Summary = false,
}: ConceptPreviewProps) {
  const parts = generateConceptParts(planningState, includeSegment2Summary);
  const [hoveredField, setHoveredField] = useState<ConceptPart['type'] | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const handleMouseEnter = (e: React.MouseEvent, fieldType: ConceptPart['type']) => {
    if (fieldType === 'static') return;

    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 8,
    });
    setHoveredField(fieldType);
  };

  const handleMouseLeave = () => {
    setHoveredField(null);
  };

  const handleClick = (fieldType: ConceptPart['type']) => {
    if (!isInteractive || fieldType === 'static' || !onEditField) return;
    onEditField(fieldType);
  };

  // Split parts into paragraphs based on \n\n
  const paragraphs: ConceptPart[][] = [];
  let currentParagraph: ConceptPart[] = [];

  parts.forEach((part) => {
    if (part.text === '\n\n') {
      if (currentParagraph.length > 0) {
        paragraphs.push(currentParagraph);
        currentParagraph = [];
      }
    } else {
      currentParagraph.push(part);
    }
  });

  if (currentParagraph.length > 0) {
    paragraphs.push(currentParagraph);
  }

  return (
    <div className="text-center text-sm font-medium text-gray-200 bg-[#0a0f1f] border border-[#1a2d4d] rounded-lg p-3">
      {paragraphs.map((paragraph, paragraphIndex) => (
        <div
          key={paragraphIndex}
          className={`leading-tight text-balance ${paragraphIndex > 0 ? 'mt-3' : ''}`}
        >
          {paragraph.map((part, partIndex) => (
            <span
              key={partIndex}
              className={`${
                part.type === 'static'
                  ? 'text-gray-200'
                  : part.isFilled
                    ? colorMap[part.type]
                    : 'text-gray-500'
              } ${
                isInteractive && part.type !== 'static'
                  ? 'cursor-pointer hover:underline hover:brightness-125 transition-all duration-300 ease-in-out hover:scale-[1.02] hover:shadow-[0_0_20px_rgba(79,70,229,0.1)]'
                  : ''
              }`}
              onMouseEnter={(e) => handleMouseEnter(e, part.type)}
              onMouseLeave={handleMouseLeave}
              onClick={() => handleClick(part.type)}
            >
              {part.text}
            </span>
          ))}
        </div>
      ))}
      {hoveredField && (
        <div
          className="fixed z-50 px-3 py-2 text-xs font-medium text-white bg-gradient-to-b from-[#0a0a0a] to-[#101010] border border-gray-600/50 rounded-lg shadow-[0_4px_16px_rgba(0,0,0,0.3)] backdrop-blur-sm pointer-events-none"
          style={{
            left: tooltipPosition.x,
            top: tooltipPosition.y,
            transform: 'translateX(-50%) translateY(-100%)',
          }}
        >
          {fieldLabels[hoveredField]}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-[#101010]" />
        </div>
      )}
    </div>
  );
}
